import { useEffect, useRef, useState } from "react";
import type { LandingStats } from "@/lib/landingStats";
import { defaultLandingStats } from "@/lib/landingStats";

interface Props {
  theme: "dark" | "light";
  lang: "fr" | "en" | "ar";
  stats: LandingStats;
}

function useCountUp(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

type StatDef =
  | { value: number; suffix: string; label: string; color: string; isText?: false; isTextSuffix?: false }
  | { value: 0; suffix: string; label: string; color: string; isText: true; isTextSuffix?: false };

function StatItem({
  value,
  suffix,
  label,
  color,
  isText,
  active,
  isDark,
}: {
  value: number;
  suffix: string;
  label: string;
  color: string;
  isText?: boolean;
  active: boolean;
  isDark: boolean;
}) {
  const count = useCountUp(value, 1200, active && !isText);
  const textMuted = isDark ? "text-white/50" : "text-gray-500";
  return (
    <div className="flex flex-col items-center gap-3 relative">
      <div
        className="absolute -bottom-4 w-24 h-8 rounded-full pointer-events-none"
        style={{ background: color, filter: "blur(20px)", opacity: 0.25 }}
      />
      <div
        className="text-[72px] md:text-[96px] font-black leading-none tracking-tight"
        style={{
          color,
          fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif",
        }}
      >
        {isText ? suffix : `${count}${suffix}`}
      </div>
      <div className={`text-sm font-medium ${textMuted}`}>{label}</div>
    </div>
  );
}

function buildStats(lang: "fr" | "en" | "ar", s: LandingStats): StatDef[] {
  const labels = {
    fr: {
      wilayas: "Wilayas",
      stations: "Stations",
      downloads: "Téléchargements",
      rating: "Note moyenne",
    },
    en: {
      wilayas: "Wilayas",
      stations: "Stations",
      downloads: "Downloads",
      rating: "Average rating",
    },
    ar: {
      wilayas: "ولاية",
      stations: "محطة",
      downloads: "تحميل",
      rating: "متوسط التقييم",
    },
  }[lang];

  const rText = `${s.rating.toFixed(1)} ★`;

  return [
    {
      value: s.wilayas,
      suffix: "",
      label: labels.wilayas,
      color: "#007AFF",
    },
    {
      value: s.stations,
      suffix: "+",
      label: labels.stations,
      color: "#34C759",
    },
    {
      value: s.downloads,
      suffix: "+",
      label: labels.downloads,
      color: "#FF9500",
    },
    {
      value: 0,
      suffix: rText,
      label: labels.rating,
      color: "#FF3B30",
      isText: true,
    },
  ];
}

export default function StatsSection({ theme, lang, stats }: Props) {
  const s = stats ?? defaultLandingStats;
  const isDark = theme === "dark";
  const isRtl = lang === "ar";
  const [active, setActive] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const statDefs = buildStats(lang, s);
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textMuted = isDark ? "text-white/60" : "text-gray-500";

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(true);
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="py-28 relative overflow-hidden"
      style={{ background: isDark ? "#000" : "#F2F2F7" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(0,122,255,0.07) 0%, transparent 60%)",
        }}
      />
      <div ref={sectionRef} className="max-w-[1280px] mx-auto px-6 md:px-10 text-center">
        <p className="text-[#007AFF] text-xs font-semibold tracking-[0.15em] uppercase mb-4">
          {lang === "fr" ? "EN CHIFFRES" : lang === "en" ? "BY THE NUMBERS" : "بالأرقام"}
        </p>
        <h2
          className={`text-4xl md:text-5xl font-bold tracking-tight mb-4 ${textPrimary}`}
          style={{ fontFamily: "-apple-system,'SF Pro Display',system-ui,sans-serif" }}
        >
          {lang === "fr" ? "Mawthoq en chiffres" : lang === "en" ? "Mawthoq by the numbers" : "موثوق بالأرقام"}
        </h2>
        <p className={`text-lg mb-16 ${textMuted}`}>
          {lang === "fr"
            ? "La plateforme de référence pour le carburant en Mauritanie."
            : lang === "en"
              ? "The reference platform for fuel in Mauritania."
              : "المنصة المرجعية للوقود في موريتانيا."}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
          {statDefs.map((row, i) => (
            <StatItem
              key={i}
              value={row.value}
              suffix={row.suffix}
              label={row.label}
              color={row.color}
              isText={row.isText}
              active={active}
              isDark={isDark}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
